import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Plus, Search, Filter, Receipt, Zap, Droplets, Phone, Wifi, Trash2, Car, Building } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useFinancasStore } from '@/stores/financas.store'


interface DespesaOperacional {
  id: string
  tipo: 'energia' | 'agua' | 'telefone' | 'internet' | 'limpeza' | 'seguranca' | 'combustivel' | 'manutencao' | 'outros'
  descricao: string
  valor: number
  data: string
  dataVencimento: string
  responsavel: string
  observacoes?: string
  status: 'pago' | 'pendente' | 'vencido'
}

const tiposDespesa = [
  { value: 'energia', label: 'Energia Elétrica', icon: Zap },
  { value: 'agua', label: 'Água e Esgoto', icon: Droplets },
  { value: 'telefone', label: 'Telefone', icon: Phone },
  { value: 'internet', label: 'Internet', icon: Wifi },
  { value: 'limpeza', label: 'Material de Limpeza', icon: Receipt },
  { value: 'seguranca', label: 'Segurança', icon: Building },
  { value: 'combustivel', label: 'Combustível', icon: Car },
  { value: 'manutencao', label: 'Manutenção Geral', icon: Receipt },
  { value: 'outros', label: 'Outros', icon: Receipt }
]

const statusColors = {
  pago: 'bg-green-600 dark:bg-green-500',
  pendente: 'bg-yellow-600 dark:bg-yellow-500',
  vencido: 'bg-destructive'
}

const statusLabels = {
  pago: 'Pago',
  pendente: 'Pendente',
  vencido: 'Vencido'
}

// Mock data
const mockDespesas: DespesaOperacional[] = [
  {
    id: '1',
    tipo: 'energia',
    descricao: 'Conta de luz - Janeiro 2024',
    valor: 450.00,
    data: '2024-01-15',
    dataVencimento: '2024-01-25',
    responsavel: 'João Silva',
    status: 'pago',
    observacoes: 'Pagamento realizado via PIX'
  },
  {
    id: '2',
    tipo: 'agua',
    descricao: 'Conta de água - Janeiro 2024',
    valor: 180.00,
    data: '2024-01-20',
    dataVencimento: '2024-02-05',
    responsavel: 'Maria Santos',
    status: 'pendente'
  },
  {
    id: '3',
    tipo: 'internet',
    descricao: 'Internet fibra - Janeiro 2024',
    valor: 120.00,
    data: '2024-01-10',
    dataVencimento: '2024-01-15',
    responsavel: 'Pedro Costa',
    status: 'vencido'
  }
]

export function DespesasOperacionaisPage() {
  const navigate = useNavigate()
  const { despesasOperacionais, updateDespesaStatus } = useFinancasStore()
  const [despesasAtualizadas, setDespesasAtualizadas] = useState<{[key: string]: DespesaOperacional['status']}>({})
  const [filtros, setFiltros] = useState({
    tipo: '',
    status: '',
    dataInicio: '',
    dataFim: '',
    busca: ''
  })

  // Função para calcular status automático baseado na data de vencimento
  const calcularStatusAutomatico = (despesa: DespesaOperacional): DespesaOperacional['status'] => {
    if (despesa.status === 'pago') return 'pago' // Se já foi pago, mantém como pago
    
    const hoje = new Date()
    const dataVencimento = new Date(despesa.dataVencimento)
    
    if (dataVencimento < hoje) {
      return 'vencido'
    } else {
      return 'pendente'
    }
  }

  // Combinar dados mock com dados do store, evitando duplicação
  const todasDespesas = useMemo(() => {
    // Aplicar atualizações de status nas despesas mock
    const despesasMockAtualizadas = mockDespesas.map(despesa => {
      const statusAtualizado = despesasAtualizadas[despesa.id]
      if (statusAtualizado) {
        return { ...despesa, status: statusAtualizado }
      }
      // Se não foi marcada como paga manualmente, aplicar cálculo automático
      if (despesa.status !== 'pago') {
        return { ...despesa, status: calcularStatusAutomatico(despesa) }
      }
      return despesa
    })

    // Aplicar status automático nas despesas do store
    const despesasStoreAtualizadas = despesasOperacionais.map(despesa => {
      if (despesa.status === 'pago') {
        return despesa
      }
      return { ...despesa, status: calcularStatusAutomatico(despesa) }
    })

    return [...despesasMockAtualizadas, ...despesasStoreAtualizadas]
  }, [despesasOperacionais, despesasAtualizadas])

  const despesasFiltradas = todasDespesas.filter(despesa => {
    if (filtros.tipo && despesa.tipo !== filtros.tipo) return false
    if (filtros.status && despesa.status !== filtros.status) return false
    if (filtros.busca && !despesa.descricao.toLowerCase().includes(filtros.busca.toLowerCase()) && 
        !despesa.responsavel.toLowerCase().includes(filtros.busca.toLowerCase())) return false
    if (filtros.dataInicio && despesa.data < filtros.dataInicio) return false
    if (filtros.dataFim && despesa.data > filtros.dataFim) return false
    return true
  })

  const totalDespesas = despesasFiltradas.reduce((sum, despesa) => sum + despesa.valor, 0)

  const handleStatusChange = (id: string, novoStatus: DespesaOperacional['status']) => {
    // Verificar se é uma despesa mock
    const despesaMock = mockDespesas.find(d => d.id === id)
    if (despesaMock) {
      // Atualizar o estado local para despesas mock
      setDespesasAtualizadas(prev => ({
        ...prev,
        [id]: novoStatus
      }))
    } else {
      // Se é uma despesa do store, atualizar normalmente
      updateDespesaStatus(id, novoStatus)
    }
  }

  const handleStatusClick = (id: string, currentStatus: DespesaOperacional['status']) => {
    // Ciclar entre os status: pendente -> pago -> vencido -> pendente
    let novoStatus: DespesaOperacional['status']
    switch (currentStatus) {
      case 'pendente':
        novoStatus = 'pago'
        break
      case 'pago':
        novoStatus = 'vencido'
        break
      case 'vencido':
        novoStatus = 'pendente'
        break
      default:
        novoStatus = 'pendente'
    }
    
    handleStatusChange(id, novoStatus)
  }

  const handleDeleteDespesa = (id: string) => {
    // Implementar lógica de exclusão
    console.log('Deletar despesa:', id)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/financas/administracao')}
            className="mr-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Receipt className="w-6 h-6 text-red-500" />
              Despesas Operacionais
            </h1>
            <p className="text-muted-foreground">
              Gestão de contas e custos operacionais da igreja
            </p>
          </div>
        </div>
        <Button 
          className="bg-destructive hover:bg-destructive/90"
          onClick={() => navigate('/financas/administracao/despesas-operacionais/cadastrar')}
        >
          <Plus className="w-4 h-4 mr-2" />
          Cadastrar Despesa
        </Button>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total de Despesas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              R$ {totalDespesas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Despesas Pagas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {despesasFiltradas.filter(d => d.status === 'pago').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Despesas Pendentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {despesasFiltradas.filter(d => d.status === 'pendente').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros e Tabela Unificados */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Despesas Cadastradas ({despesasFiltradas.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Seção de Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-muted/20 rounded-lg">
            <div>
              <Label>Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por descrição ou responsável..."
                  value={filtros.busca}
                  onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={filtros.tipo} onValueChange={(value) => setFiltros({...filtros, tipo: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {tiposDespesa.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={filtros.status} onValueChange={(value) => setFiltros({...filtros, status: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="pago">Pago</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="vencido">Vencido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data Início</Label>
              <Input
                type="date"
                value={filtros.dataInicio}
                onChange={(e) => setFiltros({...filtros, dataInicio: e.target.value})}
              />
            </div>
            <div>
              <Label>Data Fim</Label>
              <Input
                type="date"
                value={filtros.dataFim}
                onChange={(e) => setFiltros({...filtros, dataFim: e.target.value})}
              />
            </div>
          </div>

          {/* Tabela */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Tipo</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Descrição</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Responsável</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Valor</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Data</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {despesasFiltradas.map((despesa) => {
                  const TipoIcon = tiposDespesa.find(t => t.value === despesa.tipo)?.icon || Receipt
                  return (
                    <tr key={despesa.id} className="border-b">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <TipoIcon className="w-4 h-4" />
                          {tiposDespesa.find(t => t.value === despesa.tipo)?.label}
                        </div>
                      </td>
                      <td className="py-3 px-4">{despesa.descricao}</td>
                      <td className="py-3 px-4">{despesa.responsavel}</td>
                      <td className="py-3 px-4">R$ {despesa.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                      <td className="py-3 px-4">{new Date(despesa.data).toLocaleDateString('pt-BR')}</td>
                      <td className="py-3 px-4">
                        <Badge 
                          className={`${statusColors[despesa.status]} text-white cursor-pointer relative group`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleStatusClick(despesa.id, despesa.status);
                          }}
                          title="Clique para alterar o status"
                        >
                          {statusLabels[despesa.status]}
                          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                            Clique para alterar o status
                          </span>
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteDespesa(despesa.id)}
                            className="text-red-400 hover:text-red-300"
                            title="Excluir despesa"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {despesasFiltradas.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma despesa encontrada com os filtros aplicados.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}